/* ParkRoom — Card 2 della Match. Mostra parchi cani vicini sulla mappa
   + permette di creare una stanza a un punto custom (long-press sulla mappa).
   Una stanza dura 3h. Membri chattano (push notify). */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, MapPin, Plus, Users, Clock, X, Send, PawPrint,
  Crosshair, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap, hapticSuccess } from "@/lib/haptics";

const DEFAULT_CENTER = [45.4642, 9.1900];

// ---- Marker icons ----
const parkIcon = L.divIcon({
  className: "",
  html: `<div style="position:relative;height:48px;width:48px;display:flex;align-items:center;justify-content:center">
    <div style="position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle,rgba(45,186,96,0.45) 0%,rgba(45,186,96,0) 70%);animation:orio-ping 2.4s ease-out infinite"></div>
    <div style="position:relative;height:34px;width:34px;border-radius:50%;background:linear-gradient(135deg,#2DBA60 0%,#34D399 100%);border:3px solid #FFFFFF;box-shadow:0 6px 18px -4px rgba(45,186,96,0.55);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:14px">🐾</div>
  </div>`,
  iconSize: [48, 48],
  iconAnchor: [24, 24],
});
const customRoomIcon = L.divIcon({
  className: "",
  html: `<div style="position:relative;height:44px;width:44px;display:flex;align-items:center;justify-content:center">
    <div style="position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle,rgba(255,122,26,0.45) 0%,rgba(255,122,26,0) 70%);animation:orio-ping 2s ease-out infinite"></div>
    <div style="position:relative;height:32px;width:32px;border-radius:50%;background:linear-gradient(135deg,#FF5C2A 0%,#F4B83C 100%);border:3px solid #FFFFFF;box-shadow:0 6px 18px -4px rgba(255,122,26,0.55);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:13px">★</div>
  </div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});
const meIcon = L.divIcon({
  className: "",
  html: `<div style="height:18px;width:18px;border-radius:50%;background:#0EA5E9;border:3px solid white;box-shadow:0 0 0 4px rgba(14,165,233,0.30)"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

function MapClickHandler({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
}
function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 14); }, [center, map]);
  return null;
}

export default function ParkRoom({ onBack }) {
  const { user } = useAuth();
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [me, setMe] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [osmParks, setOsmParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedPin, setPickedPin] = useState(null); // {lat, lng} from map tap for custom room
  const [openRoom, setOpenRoom] = useState(null); // room obj
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const c = [p.coords.latitude, p.coords.longitude];
        setCenter(c); setMe(c);
        loadRooms(c[0], c[1]);
        loadOSMParks(c[0], c[1]);
      },
      () => { loadRooms(DEFAULT_CENTER[0], DEFAULT_CENTER[1]); loadOSMParks(DEFAULT_CENTER[0], DEFAULT_CENTER[1]); },
      { enableHighAccuracy: true, timeout: 6000 }
    );
  }, []);

  const loadRooms = async (lat, lng) => {
    try {
      const radius = user?.radius_prefs?.parks || 15;
      const { data } = await api.get("/match/park-rooms/nearby", { params: { lat, lng, radius_km: radius } });
      setRooms(data.items || []);
    } catch (e) {
      if (e.response?.status !== 402) console.error(e);
    } finally { setLoading(false); }
  };

  const loadOSMParks = async (lat, lng) => {
    try {
      const radius = user?.radius_prefs?.parks || 15;
      const { data } = await api.get("/map/poi", { params: { lat, lng, radius_km: radius } });
      const parks = (data.pois || []).filter((p) => p.type === "dog_park").slice(0, 30);
      setOsmParks(parks);
    } catch {
      setOsmParks([]);
    }
  };

  const onCreateAtOSM = async (park) => {
    setCreating(true);
    try {
      const { data } = await api.post("/match/park-rooms", {
        kind: "osm",
        lat: park.lat, lng: park.lng,
        name: park.name || "Area cani",
        park_id: String(park.id || ""),
      });
      hapticSuccess();
      toast.success("Sei nella stanza!");
      await loadRooms(center[0], center[1]);
      // open detail
      const detail = await api.get(`/match/park-rooms/${data.room_id}`);
      setOpenRoom(detail.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
    finally { setCreating(false); }
  };

  const onCreateCustom = async () => {
    if (!pickedPin) return;
    setCreating(true);
    try {
      const { data } = await api.post("/match/park-rooms", {
        kind: "custom", lat: pickedPin.lat, lng: pickedPin.lng,
        name: "Punto d'incontro",
      });
      hapticSuccess();
      toast.success("Stanza creata · invita altri padroni!");
      setPickedPin(null);
      await loadRooms(center[0], center[1]);
      const detail = await api.get(`/match/park-rooms/${data.room_id}`);
      setOpenRoom(detail.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setCreating(false); }
  };

  const openRoomById = async (rid) => {
    try {
      const { data } = await api.get(`/match/park-rooms/${rid}`);
      setOpenRoom(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden">
      <Aurora />

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-4 pb-12">
        <div className="flex items-center justify-between gap-3">
          <button onClick={onBack} data-testid="park-back"
            className="h-10 w-10 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center hover:bg-[#F4EFE6]">
            <ChevronLeft className="h-4 w-4 text-[#1A1A1A]" />
          </button>
          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] inline-flex items-center gap-1.5 justify-center">
              <MapPin className="h-3 w-3" /> Park Room
            </div>
            <h1 className="font-display text-2xl tracking-tight leading-tight mt-0.5">
              Stanze nei <span className="orio-grad-text">parchi</span>
            </h1>
          </div>
          <div className="w-10" />
        </div>

        <p className="mt-3 text-center text-[12px] text-[#6B6B6B] max-w-md mx-auto">
          Scegli un'area cani sulla mappa o tocca un punto qualsiasi per crearci una stanza. Resta aperta 3h.
        </p>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="relative mt-4 rounded-3xl overflow-hidden border border-[#F0EBE3] bg-white"
          style={{ height: "44vh", minHeight: 280, boxShadow: "0 14px 36px -16px rgba(0,0,0,0.18)" }}
          data-testid="park-map-container"
        >
          <MapContainer center={center} zoom={14} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' maxZoom={19} keepBuffer={6} updateWhenIdle={false} updateWhenZooming={false} />
            <Recenter center={pickedPin ? [pickedPin.lat, pickedPin.lng] : null} />
            <MapClickHandler onClick={(latlng) => { hapticTap(); setPickedPin({ lat: latlng.lat, lng: latlng.lng }); }} />
            {me && <Marker position={me} icon={meIcon} />}
            {osmParks.map((p) => {
              const inRoom = rooms.find(r => Math.abs(r.lat - p.lat) < 0.001 && Math.abs(r.lng - p.lng) < 0.001);
              return (
                <Marker key={`osm-${p.id || `${p.lat}-${p.lng}`}`} position={[p.lat, p.lng]} icon={parkIcon}>
                  <Popup>
                    <div style={{ minWidth: 200, fontFamily: "ui-sans-serif" }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#1A1A1A" }}>{p.name || "Area cani"}</div>
                      <div style={{ fontSize: 11, color: "#6B6B6B", margin: "4px 0 8px" }}>
                        {inRoom ? `${inRoom.members_count} padroni dentro` : "Nessuna stanza attiva"}
                      </div>
                      <button onClick={() => inRoom ? openRoomById(inRoom.id) : onCreateAtOSM(p)}
                        style={{
                          width: "100%", padding: "8px 12px", borderRadius: 999, color: "white", fontSize: 12, fontWeight: 600,
                          background: inRoom ? "linear-gradient(135deg,#2DBA60,#34D399)" : "linear-gradient(135deg,#FF7A1A,#F4B83C)",
                        }}>
                        {inRoom ? "Entra nella stanza" : "Apri stanza qui"}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {rooms.filter(r => r.kind === "custom").map((r) => (
              <Marker key={`room-${r.id}`} position={[r.lat, r.lng]} icon={customRoomIcon}>
                <Popup>
                  <div style={{ minWidth: 200, fontFamily: "ui-sans-serif" }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1A1A1A" }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: "#6B6B6B", margin: "4px 0 8px" }}>{r.members_count} padroni · {r.distance_km} km</div>
                    <button onClick={() => openRoomById(r.id)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 999, color: "white", fontSize: 12, fontWeight: 600,
                        background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
                      Entra
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
            {pickedPin && (
              <Marker position={[pickedPin.lat, pickedPin.lng]} icon={customRoomIcon}>
                <Popup>
                  <div style={{ fontFamily: "ui-sans-serif", padding: 2 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#1A1A1A" }}>Crea stanza qui</div>
                  </div>
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </motion.div>

        {/* Sticky bottom action — create custom room if pin picked */}
        <AnimatePresence>
          {pickedPin && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
              data-testid="park-create-custom-cta"
              className="mt-3 p-3 rounded-2xl border border-[#FF7A1A]/30 inline-flex items-center gap-3 w-full"
              style={{ background: "linear-gradient(135deg, rgba(255,122,26,0.08) 0%, rgba(244,184,60,0.05) 100%)" }}>
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FF7A1A, #F4B83C)" }}>
                <Crosshair className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 text-left text-[13px] text-[#1A1A1A]">
                Punto selezionato · 3h aperto a chi è nelle vicinanze
              </div>
              <button onClick={() => setPickedPin(null)} className="text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A] px-2">Annulla</button>
              <motion.button whileTap={{ scale: 0.96 }} data-testid="park-create-confirm"
                onClick={onCreateCustom} disabled={creating}
                className="px-4 py-2 rounded-full text-white text-[13px] font-semibold inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(135deg, #FF5C2A 0%, #F4B83C 100%)" }}>
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Crea
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active rooms list */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#9A9A9A] inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Stanze attive vicino a te
          </div>
          {loading ? (
            <div className="mt-3 text-[#9A9A9A] text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
          ) : rooms.length === 0 ? (
            <div className="mt-3 px-4 py-5 rounded-2xl border border-dashed border-[#F0EBE3] bg-white text-center text-[12px] text-[#6B6B6B]">
              Ancora nessuna stanza nelle vicinanze. <strong>Tocca la mappa</strong> per aprirne una.
            </div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="park-rooms-list">
              {rooms.map((r) => (
                <motion.button key={r.id} whileTap={{ scale: 0.985 }} onClick={() => openRoomById(r.id)}
                  data-testid={`park-room-${r.id}`}
                  className="w-full text-left p-3 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#F4EFE6] inline-flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center"
                    style={{ background: r.kind === "osm" ? "linear-gradient(135deg,#2DBA60,#34D399)" : "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
                    {r.kind === "osm" ? <PawPrint className="h-5 w-5 text-white" /> : <Crosshair className="h-5 w-5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[15px] text-[#1A1A1A] truncate">{r.name}</div>
                    <div className="text-[11px] text-[#6B6B6B] inline-flex items-center gap-2">
                      <Users className="h-3 w-3" /> {r.members_count} {r.members_count === 1 ? "dentro" : "dentro"}
                      <span>·</span><MapPin className="h-3 w-3" /> {r.distance_km} km
                      {r.is_member && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#34D399]/15 text-[#0F8F3B] text-[9px] font-semibold">Sei dentro</span>}
                    </div>
                  </div>
                  <span className="text-[#9A9A9A]"><ChevronLeft className="h-4 w-4 rotate-180" /></span>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {openRoom && <RoomSheet room={openRoom} onClose={() => setOpenRoom(null)} onReload={() => loadRooms(center[0], center[1])} />}
      </AnimatePresence>
    </div>
  );
}

function Aurora() {
  return (
    <>
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(110% 70% at 50% 0%, rgba(45,186,96,0.22) 0%, rgba(0,0,0,0) 60%)" }} />
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(85% 60% at 90% 100%, rgba(255,176,32,0.18) 0%, rgba(0,0,0,0) 60%)" }} />
    </>
  );
}

/* ===== Room sheet — members + chat ===== */
function RoomSheet({ room, onClose, onReload }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(room);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/match/park-rooms/${room.room.id}`);
      setDetail(data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 50);
    } catch {}
  };

  useEffect(() => {
    const t = setInterval(refresh, 8000); // light polling
    refresh();
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  useEffect(() => {
    document.body.classList.add("orio-chat-open");
    return () => document.body.classList.remove("orio-chat-open");
  }, []);

  const join = async () => {
    try {
      await api.post(`/match/park-rooms/${room.room.id}/join`);
      await refresh(); onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const leave = async () => {
    try {
      await api.post(`/match/park-rooms/${room.room.id}/leave`);
      toast.success("Hai lasciato la stanza"); onClose(); onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/match/park-rooms/${room.room.id}/messages`, { text });
      setDetail((d) => ({ ...d, messages: [...(d.messages || []), data.message] }));
      setText("");
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 50);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSending(false); }
  };

  const r = detail.room;
  const isMember = detail.is_member;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1300] flex items-end sm:items-center justify-center pb-14 sm:pb-0" data-testid="park-room-sheet">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
        transition={{ type: "spring", damping: 26, stiffness: 280 }}
        className="relative w-full sm:max-w-md mx-3 sm:mx-0 rounded-t-3xl sm:rounded-3xl bg-white border border-[#F0EBE3] overflow-hidden flex flex-col"
        style={{ maxHeight: "min(94dvh, calc(100dvh - 70px))" }}>
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-[#F0EBE3]">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: r.kind === "osm" ? "linear-gradient(135deg,#2DBA60,#34D399)" : "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
              {r.kind === "osm" ? <PawPrint className="h-5 w-5 text-white" /> : <Crosshair className="h-5 w-5 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FF7A1A]">Park Room</div>
              <h2 className="font-display text-2xl leading-tight text-[#1A1A1A] truncate">{r.name}</h2>
              <div className="text-[11px] text-[#6B6B6B] inline-flex items-center gap-2 mt-0.5">
                <Users className="h-3 w-3" /> {detail.members.length} dentro
                <span>·</span><Clock className="h-3 w-3" /> scade {new Date(r.expires_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Members strip */}
        <div className="px-5 py-3 border-b border-[#F0EBE3] overflow-x-auto">
          <div className="flex gap-2">
            {detail.members.map((m) => (
              <div key={m.user.id} className="shrink-0 text-center" data-testid={`park-member-${m.user.id}`}>
                {m.user.photo_url ? (
                  <img src={m.user.photo_url} alt="" className="h-12 w-12 rounded-2xl object-cover border border-[#F0EBE3]" />
                ) : (
                  <div className="h-12 w-12 rounded-2xl bg-[#EAE3D2] flex items-center justify-center text-[#9A9A9A]">
                    <PawPrint className="h-5 w-5" />
                  </div>
                )}
                <div className="mt-1 text-[10px] text-[#1A1A1A] truncate max-w-[60px]">{m.user.name}</div>
                {m.dogs?.[0] && <div className="text-[9px] text-[#6B6B6B] truncate max-w-[60px]">🐾 {m.dogs[0].name}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2" style={{ minHeight: 180 }}>
          {detail.messages.length === 0 ? (
            <div className="text-center text-[12px] text-[#9A9A9A] py-8">
              Stanza aperta · scrivi un saluto per rompere il ghiaccio 🐾
            </div>
          ) : (
            detail.messages.map((m) => {
              const mine = m.from_id === user?.id;
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className={`max-w-[80%] ${mine ? "ml-auto" : "mr-auto"} flex items-end gap-1.5`}>
                  {!mine && (
                    m.from_photo ? <img src={m.from_photo} className="h-6 w-6 rounded-full object-cover" alt="" />
                      : <div className="h-6 w-6 rounded-full bg-[#EAE3D2]" />
                  )}
                  <div>
                    {!mine && <div className="text-[10px] text-[#9A9A9A] ml-2 mb-0.5">{m.from_name}</div>}
                    <div className={`px-3 py-2 rounded-2xl text-[14px] ${mine ? "text-white" : "text-[#1A1A1A]"}`}
                      style={{ background: mine ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" : "#F4EFE6" }}>
                      {m.text}
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Composer / Join */}
        {isMember ? (
          <div className="border-t border-[#F0EBE3] p-3 flex items-center gap-2">
            <input data-testid="park-msg-input" value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Scrivi al gruppo…" maxLength={240}
              className="flex-1 px-3.5 py-2.5 rounded-full bg-[#FBF7F1] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]/40 text-sm text-[#1A1A1A]" />
            <motion.button whileTap={{ scale: 0.92 }} onClick={send} disabled={!text.trim() || sending} data-testid="park-msg-send"
              className="h-10 w-10 rounded-full text-white flex items-center justify-center disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
              <Send className="h-4 w-4" />
            </motion.button>
            <button onClick={leave} data-testid="park-leave" className="text-[11px] text-[#9A9A9A] hover:text-[#E94545] px-2">Esci</button>
          </div>
        ) : (
          <div className="border-t border-[#F0EBE3] p-3">
            <motion.button whileTap={{ scale: 0.97 }} onClick={join} data-testid="park-join"
              className="w-full py-3 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #2DBA60 0%, #34D399 100%)" }}>
              <PawPrint className="h-4 w-4" /> Entra nella stanza
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
